import React, { useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userData: any;
}

export default function Sidebar({ isOpen, onClose, userData }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdsLocked = false;

  const isDealer = userData?.role === 'DEALER' || !!userData?.dealerPaymentStatus;

  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      onClose();
    }
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `dash-nav-link ${isActive ? 'active' : ''}`;

  // Helper because custom specific paths might need active states even if they aren't directly in dashboard nav
  const isProfileActive = location.pathname === '/dashboard/profile';
  const isFavoritesActive = location.pathname === '/dashboard/favorites';

  return (
    <>
      <div className={`sidebar-backdrop ${isOpen ? 'show' : ''}`} onClick={onClose} />
      <aside className={`dashboard-sidebar ${isOpen ? 'show' : ''}`}>
        
        {/* Header Logo */}
        <div className="dashboard-sidebar-header">
          <img src="/images/logos/AutoPlace.png" alt="AutoPlace" height="30" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </div>

        {/* Links */}
        <div className="dashboard-sidebar-content">
          <div className="dash-nav-section">{t('dashboard.section_general', 'GENERAL')}</div>
          <NavLink to="/dashboard/overview" className={navClass} onClick={handleNavClick}>
            <i className="bi bi-grid-1x2"></i> {t('dashboard.overview')}
          </NavLink>
          <NavLink to="/dashboard/messages" className={navClass} onClick={handleNavClick}>
            <i className="bi bi-chat-text"></i> {t('dashboard.messages')}
          </NavLink>

          <div className="dash-nav-section">{t('dashboard.section_buy', 'BUYING')}</div>

          <div 
            className={`dash-nav-link ${isFavoritesActive ? 'active' : ''}`}
            onClick={() => { navigate('/dashboard/favorites'); handleNavClick(); }}
          >
            <i className="bi bi-heart"></i> {t('dashboard.saved_vehicles')}
          </div>

          <div className="dash-nav-section">{t('dashboard.section_sell', 'SELLING')}</div>
          <NavLink
            to="/dashboard/ads"
            className={({ isActive }) => `dash-nav-link ${isActive ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <i className="bi bi-tags"></i> {t('dashboard.ads', 'My Listings')}
          </NavLink>
          <NavLink to="/dashboard/add-user-listing" className="dash-nav-link" onClick={handleNavClick}>
            <i className="bi bi-plus-circle"></i> {t('dashboard.create_listing')}
          </NavLink>
          <NavLink to="/dashboard/bulk-upload" className={navClass} onClick={handleNavClick}>
            <i className="bi bi-cloud-upload"></i> {t('dashboard.bulk_upload')}
          </NavLink>

          <div className="dash-nav-section">{t('dashboard.section_credits', 'BILLING')}</div>
          <NavLink to="/dashboard/credits" className={navClass} onClick={handleNavClick}>
            <i className="bi bi-coin"></i> {t('dashboard.buy_credits', 'Credits')}
          </NavLink>

          {isDealer && (
            <>
              <div className="dash-nav-section">{t('dashboard.section_dealer', 'DEALER')}</div>
              <NavLink to="/dashboard/subscription" className={navClass} onClick={handleNavClick}>
                <i className="bi bi-shop"></i> {t('dashboard.subscription')}
              </NavLink>
              <NavLink to="/dashboard/invoices" className={navClass} onClick={handleNavClick}>
                <i className="bi bi-receipt"></i> {t('dashboard.invoices')}
              </NavLink>
            </>
          )}

          <div className="dash-nav-section">{t('dashboard.section_my_profile', 'ACCOUNT')}</div>
          <div 
            className={`dash-nav-link ${isProfileActive ? 'active' : ''}`} 
            onClick={() => { navigate('/dashboard/profile'); handleNavClick(); }}
          >
            <i className="bi bi-person-fill"></i> {t('dashboard.edit_profile', 'My Profile')}
          </div>
          <NavLink to="/dashboard/settings" className={navClass} onClick={handleNavClick}>
            <i className="bi bi-gear"></i> {t('dashboard.settings')}
          </NavLink>
        </div>

        {/* User Card */}
        <div className="dash-user-card" onClick={() => navigate('/dashboard/settings')} style={{ cursor: 'pointer' }}>
          <div className="dash-user-avatar">
            {userData?.firstName?.[0]?.toUpperCase() || userData?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="dash-user-info flex-grow-1 text-truncate">
            <p className="fw-bold text-truncate" style={{ fontSize: 14 }}>
              {userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : t('dashboard.my_account')}
            </p>
            <p className="text-muted text-truncate" style={{ fontSize: 12 }}>
              {isDealer ? t('dashboard.dealer_label') : t('dashboard.member_label')}
            </p>
          </div>
        </div>

      </aside>
    </>
  );
}
